import { ElementType, Fragment, forwardRef, ReactNode, Ref, useId } from 'react';
import styled from 'styled-components';
import { PolymorphicComponentPropWithRef } from '../../utils/polymorphic_component_prop_with_ref';
import { Icon, IconDictionary } from '../icon';
import { Portal } from '../portal';
import { Spinner } from '../spinner';
import { VisuallyHidden } from '../visually_hidden';
import { theme } from './button.theme';
import { HeadlessButton, HeadlessButtonProps } from './headless_button.component';

/**
 * ## @visiba/cellula-button
 *
 * CellulaButton
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'xLarge' | 'large' | 'medium' | 'small' | 'xSmall';
type ButtonIconPosition = 'leading' | 'trailing';

// The button component does not include "cursor: pointer;" due to
// official standards specifying that: "The cursor is a pointer that indicates
// a link." https://www.w3.org/TR/CSS21/ui.html#propdef-cursor
//
// Microsoft and Apple also specifies that the "correct" interaction is to
// reserve the pointing hand to URL links
// https://docs.microsoft.com/en-gb/windows/win32/uxguide/inter-mouse?redirectedfrom=MSDN
// https://developer.apple.com/design/human-interface-guidelines/macos/user-interaction/mouse-and-trackpad/
//
// If you'd want to have buttons with cursors, that's fine, but you'll need to
// extend the button component in order to get that interaction
const StyledButton = styled(HeadlessButton)`
	--font: ${theme.getValue('baseFont')};
	--background: transparent;
	--backgroundHover: transparent;
	--backgroundActive: transparent;
	--textColor: #000;
	--border: none;
	--minHeight: auto;
	--minWidth: auto;
	--paddingVertical: 0;
	--paddingLeft: 0;
	--paddingRight: 0;
	--iconMarginLeft: 0;
	--iconMarginRight: 0;
	--iconSize: 0;

	user-select: none;
	-webkit-tap-highlight-color: transparent;
	-webkit-user-drag: none;
	-webkit-touch-callout: none;

	margin: 0;
	width: auto;
	overflow: visible;
	color: inherit;
	line-height: normal;
	-webkit-font-smoothing: inherit;
	-moz-osx-font-smoothing: inherit;
	-webkit-appearance: none;
	transition:
		background 150ms ease-in,
		box-shadow 150ms ease-in;
	display: flex;
	align-items: center;
	justify-content: center;
	outline: none;
	word-wrap: break-word;
	word-break: break-word;
	border-radius: ${theme.getValue('baseBorderRadius')};
	padding: var(--paddingVertical) var(--paddingRight) var(--paddingVertical) var(--paddingLeft);
	font: var(--font);
	background: var(--background);
	border: var(--border);
	min-height: var(--minHeight);
	min-width: var(--minWidth);
	color: var(--textColor);

	&:not([aria-disabled='true'], [aria-busy='true']) {
		&:hover {
			background: var(--backgroundHover);
		}
		&:active {
			background: var(--backgroundActive);
		}
	}

	& > .cellula-button__icon {
		flex: 0 0 auto;
		padding-left: var(--iconMarginLeft);
		padding-right: var(--iconMarginRight);

		& > svg {
			width: var(--iconSize);
			height: var(--iconSize);
		}
	}

	& > .cellula-button__spinner {
		flex: 0 0 auto;
		padding-right: ${theme.getValue('baseIconMarginToText')};

		& > div {
			width: var(--iconSize);
			height: var(--iconSize);
		}
	}

	// Icon
	&[data-icon-position='leading'] {
		--iconMarginLeft: 0;
		--iconMarginRight: ${theme.getValue('baseIconMarginToText')};
	}
	&[data-icon-position='trailing'] {
		--iconMarginLeft: ${theme.getValue('baseIconMarginToText')};
		--iconMarginRight: 0;
	}

	// Variants
	&[data-variant='primary'] {
		--background: ${theme.getValue('primaryBackground')};
		--backgroundHover: ${theme.getValue('primaryHoverBackground')};
		--backgroundActive: ${theme.getValue('primaryActiveBackground')};
		--textColor: ${theme.getValue('primaryTextColor')};
	}
	&[data-variant='secondary'] {
		--backgroundHover: ${theme.getValue('secondaryHoverBackground')};
		--backgroundActive: ${theme.getValue('secondaryActiveBackground')};
		--textColor: ${theme.getValue('secondaryTextColor')};
		--border: 2px solid ${theme.getValue('secondaryBorderColor')};
	}
	&[data-variant='ghost'] {
		--backgroundHover: ${theme.getValue('ghostHoverBackground')};
		--backgroundActive: ${theme.getValue('ghostActiveBackground')};
		--textColor: ${theme.getValue('ghostTextColor')};
	}
	&[data-variant='danger'] {
		--background: ${theme.getValue('dangerBackground')};
		--backgroundHover: ${theme.getValue('dangerHoverBackground')};
		--backgroundActive: ${theme.getValue('dangerActiveBackground')};
		--textColor: ${theme.getValue('dangerTextColor')};
	}

	// Sizes
	&[data-size='xSmall'] {
		--paddingVertical: ${theme.getValue('xSmallPaddingVertical')};
		--paddingLeft: ${theme.getValue('xSmallPaddingHorizontal')};
		--paddingRight: ${theme.getValue('xSmallPaddingHorizontal')};
		--minHeight: ${theme.getValue('xSmallMinHeight')};
		--minWidth: ${theme.getValue('xSmallMinWidth')};
		--font: ${theme.getValue('xSmallFont')};
		--iconSize: ${theme.getValue('xSmallIconSize')};

		&[data-variant='secondary'] {
			--paddingVertical: calc(${theme.getValue('xSmallPaddingVertical')} - 2px);
		}
		&[aria-busy] {
			--paddingLeft: ${theme.getValue('xSmallIconPadding')};
		}
		&[data-icon][data-icon-position='leading'] {
			--paddingLeft: ${theme.getValue('xSmallIconPadding')};
		}
		&[data-icon][data-icon-position='trailing'] {
			--paddingRight: ${theme.getValue('xSmallIconPadding')};
		}
	}
	&[data-size='small'] {
		--paddingVertical: ${theme.getValue('smallPaddingVertical')};
		--paddingLeft: ${theme.getValue('smallPaddingHorizontal')};
		--paddingRight: ${theme.getValue('smallPaddingHorizontal')};
		--minHeight: ${theme.getValue('smallMinHeight')};
		--minWidth: ${theme.getValue('smallMinWidth')};
		--font: ${theme.getValue('smallFont')};
		--iconSize: ${theme.getValue('smallIconSize')};

		&[data-variant='secondary'] {
			--paddingVertical: calc(${theme.getValue('smallPaddingVertical')} - 2px);
		}
		&[aria-busy] {
			--paddingLeft: ${theme.getValue('smallIconPadding')};
		}
		&[data-icon][data-icon-position='leading'] {
			--paddingLeft: ${theme.getValue('smallIconPadding')};
		}
		&[data-icon][data-icon-position='trailing'] {
			--paddingRight: ${theme.getValue('smallIconPadding')};
		}
	}
	&[data-size='medium'] {
		--paddingVertical: ${theme.getValue('mediumPaddingVertical')};
		--paddingLeft: ${theme.getValue('mediumPaddingHorizontal')};
		--paddingRight: ${theme.getValue('mediumPaddingHorizontal')};
		--minHeight: ${theme.getValue('mediumMinHeight')};
		--minWidth: ${theme.getValue('mediumMinWidth')};
		--font: ${theme.getValue('mediumFont')};
		--iconSize: ${theme.getValue('mediumIconSize')};

		&[data-variant='secondary'] {
			--paddingVertical: calc(${theme.getValue('mediumPaddingVertical')} - 2px);
		}
		&[aria-busy] {
			--paddingLeft: ${theme.getValue('mediumIconPadding')};
		}
		&[data-icon][data-icon-position='leading'] {
			--paddingLeft: ${theme.getValue('mediumIconPadding')};
		}
		&[data-icon][data-icon-position='trailing'] {
			--paddingRight: ${theme.getValue('mediumIconPadding')};
		}
	}
	&[data-size='large'] {
		--paddingVertical: ${theme.getValue('largePaddingVertical')};
		--paddingLeft: ${theme.getValue('largePaddingHorizontal')};
		--paddingRight: ${theme.getValue('largePaddingHorizontal')};
		--minHeight: ${theme.getValue('largeMinHeight')};
		--minWidth: ${theme.getValue('largeMinWidth')};
		--font: ${theme.getValue('largeFont')};
		--iconSize: ${theme.getValue('largeIconSize')};

		&[data-variant='secondary'] {
			--paddingVertical: calc(${theme.getValue('largePaddingVertical')} - 2px);
		}
		&[aria-busy] {
			--paddingLeft: ${theme.getValue('largeIconPadding')};
		}
		&[data-icon][data-icon-position='leading'] {
			--paddingLeft: ${theme.getValue('largeIconPadding')};
		}
		&[data-icon][data-icon-position='trailing'] {
			--paddingRight: ${theme.getValue('largeIconPadding')};
		}
	}
	&[data-size='xLarge'] {
		--paddingVertical: ${theme.getValue('xLargePaddingVertical')};
		--paddingLeft: ${theme.getValue('xLargePaddingHorizontal')};
		--paddingRight: ${theme.getValue('xLargePaddingHorizontal')};
		--minHeight: ${theme.getValue('xLargeMinHeight')};
		--minWidth: ${theme.getValue('xLargeMinWidth')};
		--font: ${theme.getValue('xLargeFont')};
		--iconSize: ${theme.getValue('xLargeIconSize')};

		&[data-variant='secondary'] {
			--paddingVertical: calc(${theme.getValue('xLargePaddingVertical')} - 2px);
		}
		&[aria-busy] {
			--paddingLeft: ${theme.getValue('xLargeIconPadding')};
		}
		&[data-icon][data-icon-position='leading'] {
			--paddingLeft: ${theme.getValue('xLargeIconPadding')};
		}
		&[data-icon][data-icon-position='trailing'] {
			--paddingRight: ${theme.getValue('xLargeIconPadding')};
		}
	}

	&:focus-visible {
		outline: 2px solid ${theme.getValue('baseFocusRingColor')};
		outline-offset: 2px;
	}

	&[aria-disabled='true'] {
		opacity: 0.6;
		cursor: not-allowed;
	}

	&[aria-busy='true'] {
		opacity: 0.6;
		cursor: progress;
	}
`;

interface Events {
	onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
	onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseMove?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseDown?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseUp?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseOver?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onKeyUp?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
	onTouchEnd?: (event: React.TouchEvent<HTMLButtonElement>) => void;
	onTouchMove?: (event: React.TouchEvent<HTMLButtonElement>) => void;
	onTouchStart?: (event: React.TouchEvent<HTMLButtonElement>) => void;
}

interface WithoutDisabledProps {
	disabled?: never;
	disabledText?: never;
	'aria-label'?: string;
}
interface DisabledPropsWithDisabledText {
	/**
	 * Use to make the button disabled. If set to `true` it will disable all user
	 * interaction events (it can be bypassed by `allowPassThrough`).
	 */
	disabled: boolean;

	/**
	 * The purpose with this is to provide a, visually hidden, accessible
	 * explanation why the button is disabled. So that users who can't see the
	 * visually context, e.g. screen reader users, know why they can't click the
	 * button. If no `aria-label` is set, this property will be required if the
	 * `disabled`-property is set to `true`.
	 */
	disabledText: string;
	'aria-label'?: never;
}
interface DisabledPropsWithAriaLabel {
	disabled: boolean;
	disabledText?: never;
	'aria-label': string;
}

type IntrinsicButtonProps = HeadlessButtonProps & {
	/**
	 * The content in the button.
	 */
	children: ReactNode;

	/**
	 * The visual representation of the button.
	 *
	 * @default 'primary'
	 */
	variant?: ButtonVariant;

	/**
	 * The visual size of the button.
	 *
	 * @default 'medium'
	 */
	size?: ButtonSize;

	/**
	 * Use to indicate if the button is in loading state or not. If set to `true`
	 * it will disable all user interaction events (it can be bypassed by
	 * `allowPassThrough`).
	 */
	loading?: boolean;

	/**
	 * Use to add a icon in the button. The position of the icon can be controlled
	 * with `iconPosition`-property.
	 */
	icon?: keyof IconDictionary;

	/**
	 * Specifies the position of the icon. Leading or trailing. It has only effect
	 * in combination with `icon`-property.
	 * @default leading
	 */
	iconPosition?: ButtonIconPosition;
} & Events &
	(WithoutDisabledProps | DisabledPropsWithAriaLabel | DisabledPropsWithDisabledText);

type ButtonProps<C extends ElementType> = PolymorphicComponentPropWithRef<C, IntrinsicButtonProps>;

function CellulaButton<C extends ElementType = 'button'>(
	{
		variant = 'primary',
		iconPosition = 'leading',
		size = 'medium',
		disabled,
		loading,
		icon,
		disabledText,
		children,
		as,
		...rest
	}: ButtonProps<C>,
	ref: Ref<HTMLButtonElement>,
): JSX.Element {
	let iconElement;
	if (icon) {
		iconElement = (
			<span className='cellula-button__icon'>
				<Icon type={icon} />
			</span>
		);
	}

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const id = useId();

	return (
		<Fragment>
			<StyledButton
				as={as}
				disabled={disabled || loading}
				busy={loading}
				{...rest}
				aria-describedby={disabled && disabledText ? `${id}-disabled-text` : rest['aria-describedby']}
				ref={ref}
				data-variant={variant}
				data-size={size}
				data-icon={!!icon || undefined}
				data-icon-position={iconPosition}
			>
				{icon && !loading && iconPosition === 'leading' && iconElement}
				{loading && (
					<span className='cellula-button__spinner' aria-hidden='true'>
						<Spinner size='medium' />
					</span>
				)}
				{children}
				{icon && !loading && iconPosition === 'trailing' && iconElement}
			</StyledButton>
			{/*
				This can look a bit strange, putting the disabledText in a Portal and using
				aria-describedby to make the text be read.
				But we are doing this to make screen readers read this better.

				If we put the disabledText into the button as Visually hidden right after the normal
				visible text. A screen reader will read the disabledText before it reads that the
				button is disabled and it can be a bit weird:
				"Button label This button is disabled because... unavailable busy"

				  Visual text              Disabled text               Disabled state   Busy state
				      |                         |                            |              |
				"Button label     This button is disabled because...    unavailable       busy"

				With the aria-describedby approach a screen reader will read the button like this:
				"Button label unavailable busy This button is disabled because..."

				  Visual text   Disabled state   Busy state              Disabled text
				      |                |              |                       |
				"Button label     unavailable       busy      This button is disabled because..."
			*/}
			{disabled && disabledText ? (
				<Portal>
					<VisuallyHidden id={`${id}-disabled-text`}>{disabledText}</VisuallyHidden>
				</Portal>
			) : null}
		</Fragment>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component for allowing users to preform certain actions.
 */
export const Button = forwardRef(
	CellulaButton,
) as typeof CellulaButton; /* This cast is necessary to unwrap the intrinsic types of the polymorphed component */
export type { ButtonProps, IntrinsicButtonProps };
